import { useEffect, useState, useCallback, forwardRef, type HTMLAttributes } from "react";

export type StarDisplaySize = "sm" | "md" | "lg" | "xl";

export interface StarDisplayProps extends HTMLAttributes<HTMLDivElement> {
  /** Number of stars earned (0-3) */
  stars: 0 | 1 | 2 | 3;
  /** Maximum stars (always 3 for JumpBean) */
  maxStars?: 3;
  /** Size of the stars */
  size?: StarDisplaySize;
  /** Whether to animate the star filling sequence */
  animate?: boolean;
  /** Delay before animation starts (ms) */
  animationDelay?: number;
  /** Callback when all star animations complete */
  onAnimationComplete?: () => void;
  /** Whether to show sparkle effects */
  showSparkles?: boolean;
  /** Whether to play sound effects (requires audio context) */
  playSound?: boolean;
}

/**
 * StarDisplay - Animated star rating with fill sequence
 *
 * Features per issue #31:
 * 1. Stars appear empty initially
 * 2. Each earned star fills with staggered animation
 * 3. Sparkle effect on filled stars
 * 4. Sound effect hook for each star (managed externally)
 *
 * @example
 * // Static display
 * <StarDisplay stars={2} />
 *
 * @example
 * // Animated result after completing a challenge
 * <StarDisplay
 *   stars={3}
 *   size="xl"
 *   animate
 *   showSparkles
 *   onAnimationComplete={() => console.log('Done!')}
 * />
 */
export const StarDisplay = forwardRef<HTMLDivElement, StarDisplayProps>(
  (
    {
      stars,
      maxStars = 3,
      size = "md",
      animate = false,
      animationDelay = 0,
      onAnimationComplete,
      showSparkles = true,
      className = "",
      ...props
    },
    ref
  ) => {
    // Track which stars have been revealed
    const [revealedStars, setRevealedStars] = useState<number[]>([]);
    const [animationPhase, setAnimationPhase] = useState<"waiting" | "animating" | "complete">(
      animate ? "waiting" : "complete"
    );

    // Size configurations matching JumpBean design system
    const sizeStyles = {
      sm: {
        container: "gap-2",
        star: "w-8 h-8",
        sparkleSize: "w-3 h-3",
        glowBlur: "blur-sm",
      },
      md: {
        container: "gap-3",
        star: "w-12 h-12",
        sparkleSize: "w-4 h-4",
        glowBlur: "blur-md",
      },
      lg: {
        container: "gap-4",
        star: "w-16 h-16",
        sparkleSize: "w-5 h-5",
        glowBlur: "blur-lg",
      },
      xl: {
        container: "gap-5",
        star: "w-24 h-24",
        sparkleSize: "w-6 h-6",
        glowBlur: "blur-xl",
      },
    };

    const currentSize = sizeStyles[size];

    // Handle star reveal animation sequence
    useEffect(() => {
      if (!animate) {
        // No animation - show all earned stars immediately
        setRevealedStars(Array.from({ length: stars }, (_, i) => i));
        setAnimationPhase("complete");
        return;
      }

      // Reset state for new animation
      setRevealedStars([]);
      setAnimationPhase("waiting");

      // Initial delay before starting
      const startTimer = setTimeout(() => {
        setAnimationPhase("animating");
      }, animationDelay);

      return () => clearTimeout(startTimer);
    }, [animate, animationDelay, stars]);

    // Stagger star reveals during animation phase
    useEffect(() => {
      if (animationPhase !== "animating") return;

      const timers: ReturnType<typeof setTimeout>[] = [];

      for (let i = 0; i < stars; i++) {
        const timer = setTimeout(() => {
          setRevealedStars((prev) => [...prev, i]);

          // Check if this is the last star
          if (i === stars - 1) {
            // Add small delay after last star before calling complete
            setTimeout(() => {
              setAnimationPhase("complete");
              onAnimationComplete?.();
            }, 500);
          }
        }, i * 400); // 400ms between each star

        timers.push(timer);
      }

      // If no stars to reveal, complete immediately
      if (stars === 0) {
        setAnimationPhase("complete");
        onAnimationComplete?.();
      }

      return () => timers.forEach(clearTimeout);
    }, [animationPhase, stars, onAnimationComplete]);

    const isStarFilled = useCallback(
      (index: number) => {
        if (!animate || animationPhase === "complete") {
          return index < stars;
        }
        return revealedStars.includes(index);
      },
      [animate, animationPhase, stars, revealedStars]
    );

    const isStarRevealing = useCallback(
      (index: number) => {
        if (!animate) return false;
        // Star is "revealing" if it was just added to revealed list
        return revealedStars.length - 1 === index && animationPhase === "animating";
      },
      [animate, revealedStars, animationPhase]
    );

    return (
      <div
        ref={ref}
        role="img"
        aria-label={`${stars} out of ${maxStars} stars`}
        className={`
          inline-flex items-center justify-center
          ${currentSize.container}
          ${className}
        `}
        {...props}
      >
        {Array.from({ length: maxStars }, (_, index) => {
          const filled = isStarFilled(index);
          const revealing = isStarRevealing(index);

          return (
            <Star
              key={index}
              filled={filled}
              revealing={revealing}
              index={index}
              size={currentSize.star}
              sparkleSize={currentSize.sparkleSize}
              glowBlur={currentSize.glowBlur}
              showSparkles={showSparkles && filled}
            />
          );
        })}
      </div>
    );
  }
);

StarDisplay.displayName = "StarDisplay";

/**
 * Individual Star with animation states
 */
function Star({
  filled,
  revealing,
  index,
  size,
  sparkleSize,
  glowBlur,
  showSparkles,
}: {
  filled: boolean;
  revealing: boolean;
  index: number;
  size: string;
  sparkleSize: string;
  glowBlur: string;
  showSparkles: boolean;
}) {
  const [showLocalSparkles, setShowLocalSparkles] = useState(false);

  // Trigger sparkles when star is revealed
  useEffect(() => {
    if (revealing && showSparkles) {
      setShowLocalSparkles(true);
      const timer = setTimeout(() => setShowLocalSparkles(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [revealing, showSparkles]);

  return (
    <div
      className={`
        relative
        ${size}
        transition-all duration-300
        ${filled ? "scale-100" : "scale-90"}
        ${revealing ? "animate-star-pop" : ""}
      `}
    >
      {/* Glow effect behind filled stars */}
      {filled && (
        <div
          className={`
            absolute inset-0
            rounded-full
            bg-sunshine-400/40
            ${glowBlur}
            scale-150
            -z-10
            ${revealing ? "animate-pulse" : ""}
          `}
        />
      )}

      {/* Star SVG */}
      <svg viewBox="0 0 48 48" className="w-full h-full drop-shadow-md" aria-hidden="true">
        <defs>
          {/* Golden gradient for filled stars */}
          <linearGradient id={`starGoldGradient-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFEE58" />
            <stop offset="30%" stopColor="#FFD93D" />
            <stop offset="70%" stopColor="#FFC107" />
            <stop offset="100%" stopColor="#FFA000" />
          </linearGradient>

          {/* Shine overlay */}
          <linearGradient id={`starShine-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>

          {/* Filter for sparkle glow */}
          <filter id={`starGlow-${index}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Star path */}
        <path
          d="M24 4l5.8 11.76L42 17.52l-9 8.77 2.12 12.38L24 32.26l-11.12 6.41L15 26.29 6 17.52l12.2-1.76L24 4z"
          fill={filled ? `url(#starGoldGradient-${index})` : "#E2DDD6"}
          stroke={filled ? "#FFA000" : "#C4BDB3"}
          strokeWidth="1.5"
          strokeLinejoin="round"
          filter={filled ? `url(#starGlow-${index})` : undefined}
          className={`
            transition-all duration-500
            ${filled ? "opacity-100" : "opacity-60"}
          `}
        />

        {/* Inner shine for filled stars */}
        {filled && (
          <path
            d="M24 8l4 8.5 9 1.3-6.5 6.3 1.5 9L24 28.5l-8 4.6 1.5-9L11 17.8l9-1.3L24 8z"
            fill={`url(#starShine-${index})`}
            className="transition-opacity duration-300"
          />
        )}
      </svg>

      {/* Sparkle effects */}
      {showLocalSparkles && (
        <>
          <Sparkle
            className={`absolute -top-2 -right-1 ${sparkleSize} text-sunshine-300`}
            delay={0}
          />
          <Sparkle
            className={`absolute -bottom-1 -left-2 ${sparkleSize} text-sunshine-400`}
            delay={100}
          />
          <Sparkle
            className={`absolute top-1/4 -left-1 ${sparkleSize} text-white`}
            delay={200}
          />
          <Sparkle
            className={`absolute top-0 right-1/4 ${sparkleSize} text-sunshine-200`}
            delay={150}
          />
          <Sparkle
            className={`absolute -bottom-2 right-0 ${sparkleSize} text-yellow-100`}
            delay={250}
          />
        </>
      )}

      {/* Persistent subtle sparkles for filled stars */}
      {filled && showSparkles && !showLocalSparkles && (
        <>
          <Sparkle
            className={`absolute -top-1 right-0 w-2 h-2 text-sunshine-300/60`}
            delay={index * 200}
            persistent
          />
          <Sparkle
            className={`absolute bottom-0 -left-1 w-2 h-2 text-sunshine-400/60`}
            delay={index * 200 + 500}
            persistent
          />
        </>
      )}
    </div>
  );
}

/**
 * Animated sparkle decoration
 */
function Sparkle({
  className = "",
  delay = 0,
  persistent = false,
}: {
  className?: string;
  delay?: number;
  persistent?: boolean;
}) {
  return (
    <svg
      className={`
        ${className}
        ${persistent ? "animate-sparkle" : "animate-[sparkle_0.6s_ease-out_forwards]"}
        pointer-events-none
      `}
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{
        animationDelay: `${delay}ms`,
        animationIterationCount: persistent ? "infinite" : 1,
      }}
      aria-hidden="true"
    >
      <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
    </svg>
  );
}

export default StarDisplay;
